/**
 * SubCard Component
 *
 * A simple card layout for displaying content with border by default.
 * - Supports interactive mode with selection states and click behavior
 * - Use SubCard.Divider for adding dividers between content sections
 *
 * Props:
 *   - children: ReactNode (card content)
 *   - noBorder: boolean (hide border, border shows by default)
 *   - selected: boolean (show selected state with indigo border)
 *   - onClick: function (makes card clickable)
 *   - className: string (additional classes)
 *
 * Example:
 * ```tsx
 * <SubCard>
 *   <div>Section 1</div>
 *   <SubCard.Divider />
 *   <div>Section 2</div>
 * </SubCard>
 *
 * <SubCard noBorder>
 *   <div>No border card</div>
 * </SubCard>
 * ```
 */
"use client";
import React from "react";
import { cn } from "@/lib/utils";

interface SubCardProps {
  children?: React.ReactNode;
  noBorder?: boolean;
  selected?: boolean;
  onClick?: () => void;
  className?: string;
}

interface DividerProps {
  className?: string;
}

const Divider = ({ className }: DividerProps) => (
  <hr className={cn("my-3 border-border/50", className)} />
);

const SubCard = ({
  children,
  noBorder = false,
  selected = false,
  onClick,
  className,
}: SubCardProps) => {
  const isInteractive = Boolean(onClick);

  const baseClasses =
    "transition-colors bg-card text-card-foreground duration-200";

  const borderClasses = !noBorder
    ? cn(
        "py-3 px-3 rounded-lg border",
        selected
          ? "border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/20"
          : "border-border/50",
        isInteractive && "hover:bg-muted/50 cursor-pointer",
      )
    : "";

  const Component = isInteractive ? "button" : "div";

  return (
    <Component
      type={isInteractive ? "button" : undefined}
      onClick={onClick}
      className={cn(
        baseClasses,
        borderClasses,
        isInteractive && "w-full text-left",
        className,
      )}
    >
      {children}
    </Component>
  );
};

SubCard.Divider = Divider;

export default SubCard;
